// =============================================================================
// MeuDriver — Agenda Exceptions Card (block dates) + Copy week
// =============================================================================

import { useState } from 'react'
import { CalendarOff, Copy, PlusCircle, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDate } from '@/lib/utils'
import type { AvailabilityExceptionRow } from '@/types/database'

interface Props {
  exceptions: AvailabilityExceptionRow[]
  onAdd: (date: string, startTime: string, endTime: string) => Promise<void> | void
  onDelete: (id: string) => Promise<void> | void
  onCopyWeek: (weeks: number) => Promise<void> | void
  saving: boolean
  copying: boolean
}

export function AgendaExceptionsCard({
  exceptions, onAdd, onDelete, onCopyWeek, saving, copying,
}: Props) {
  const [date, setDate] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [copyWeeks, setCopyWeeks] = useState(1)

  async function handleAdd() {
    if (!date) return
    await onAdd(date, start, end)
    setDate('')
    setStart('')
    setEnd('')
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarOff className="h-4 w-4 text-red-500" />
            Bloqueio de datas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Bloqueie um dia inteiro ou um horário específico.
          </p>

          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Data</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-8 text-sm"
              min={format(new Date(), 'yyyy-MM-dd')}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Início (opcional)</Label>
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 text-xs" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Fim (opcional)</Label>
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 text-xs" />
            </div>
          </div>

          <Button className="w-full" variant="destructive" size="sm" onClick={handleAdd} disabled={!date || saving}>
            <PlusCircle className="h-4 w-4 mr-2" />
            {saving ? 'Bloqueando...' : 'Bloquear data'}
          </Button>

          {exceptions.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">Nenhum bloqueio cadastrado.</p>
          ) : (
            <ul className="space-y-1.5 mt-2">
              {exceptions.map((exc) => (
                <li key={exc.id} className="flex items-center justify-between rounded-md border border-red-100 bg-red-50 px-3 py-1.5 text-sm">
                  <div>
                    <span className="text-gray-700 font-medium">{formatDate(exc.date, "d 'de' MMM yyyy")}</span>
                    {exc.start_time && exc.end_time && (
                      <p className="text-[11px] text-muted-foreground">
                        {exc.start_time.slice(0, 5)} – {exc.end_time.slice(0, 5)}
                      </p>
                    )}
                    {!exc.start_time && <p className="text-[11px] text-muted-foreground">Dia inteiro</p>}
                  </div>
                  <button onClick={() => onDelete(exc.id)} className="text-muted-foreground hover:text-red-500 transition-colors ml-2">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Copy className="h-4 w-4 text-brand-600" />
            Copiar bloqueios
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Replica os bloqueios desta semana para as próximas semanas.</p>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={12}
              value={copyWeeks}
              onChange={(e) => setCopyWeeks(Math.max(1, Math.min(12, Number(e.target.value))))}
              className="h-8 w-16 text-sm text-center"
            />
            <span className="text-sm text-muted-foreground">semana(s)</span>
          </div>
          <Button className="w-full" variant="outline" size="sm" onClick={() => onCopyWeek(copyWeeks)} disabled={copying}>
            <Copy className="h-4 w-4 mr-2" />
            {copying ? 'Copiando...' : 'Copiar bloqueios'}
          </Button>
        </CardContent>
      </Card>
    </>
  )
}
